import { useFrame } from "@react-three/fiber";
import { useMemo } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useObserverStore } from "../../stores/observerStore";
import { smoothedCameraRef } from "./useViewportSync";

interface LayerConfig {
	count: number;
	size: number;
	opacity: number;
	parallaxFactor: number;
	color: THREE.Color;
}

const GALAXY_SPREAD = 8000;
const SYSTEM_SPREAD = 3000;

const LAYERS_FULL: LayerConfig[] = [
	{
		count: 6000,
		size: 0.8,
		opacity: 0.25,
		parallaxFactor: 0.02,
		color: new THREE.Color("#8890b8"),
	},
	{
		count: 4200,
		size: 1.2,
		opacity: 0.35,
		parallaxFactor: 0.05,
		color: new THREE.Color("#b0b8e0"),
	},
	{ count: 2400, size: 1.8, opacity: 0.45, parallaxFactor: 0.1, color: new THREE.Color("#c8d0f8") },
	{
		count: 1200,
		size: 2.8,
		opacity: 0.55,
		parallaxFactor: 0.16,
		color: new THREE.Color("#e0e8ff"),
	},
	{ count: 360, size: 4, opacity: 0.75, parallaxFactor: 0.25, color: new THREE.Color("#ffffff") },
];

const LAYERS_LOW: LayerConfig[] = [
	{
		count: 3000,
		size: 0.8,
		opacity: 0.25,
		parallaxFactor: 0.03,
		color: new THREE.Color("#8890b8"),
	},
	{ count: 1200, size: 1.5, opacity: 0.4, parallaxFactor: 0.1, color: new THREE.Color("#b0b8e0") },
	{ count: 360, size: 3, opacity: 0.6, parallaxFactor: 0.2, color: new THREE.Color("#e0e8ff") },
	{ count: 120, size: 4, opacity: 0.7, parallaxFactor: 0.25, color: new THREE.Color("#ffffff") },
];

// --- Custom ShaderMaterial: per-vertex parallax, size, opacity ---

const VERTEX_SHADER = /* glsl */ `
	attribute float aParallaxFactor;
	attribute float aSize;
	attribute float aOpacity;
	attribute vec3 color;

	uniform vec2 uCameraOffset;

	varying float vOpacity;
	varying vec3 vColor;

	void main() {
		vColor = color;
		vOpacity = aOpacity;

		vec3 pos = position;
		pos.xy += uCameraOffset * aParallaxFactor;

		vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
		gl_PointSize = aSize;
		gl_Position = projectionMatrix * mvPosition;
	}
`;

const FRAGMENT_SHADER = /* glsl */ `
	varying float vOpacity;
	varying vec3 vColor;

	void main() {
		gl_FragColor = vec4(vColor, vOpacity);
	}
`;

export function ParallaxStars(): React.JSX.Element {
	const { isLowPower } = useDeviceCapabilities();
	const viewMode = useObserverStore((s) => s.viewMode);
	const layers = isLowPower ? LAYERS_LOW : LAYERS_FULL;
	const spread = viewMode === "system" ? SYSTEM_SPREAD : GALAXY_SPREAD;

	const { positions, colors, parallaxFactors, sizes, opacities } = useMemo(() => {
		const totalCount = layers.reduce((sum, l) => sum + l.count, 0);
		const pos = new Float32Array(totalCount * 3);
		const col = new Float32Array(totalCount * 3);
		const pf = new Float32Array(totalCount);
		const sz = new Float32Array(totalCount);
		const op = new Float32Array(totalCount);

		let offset = 0;
		for (const config of layers) {
			for (let i = 0; i < config.count; i++) {
				const idx = offset + i;
				pos[idx * 3] = (Math.random() - 0.5) * spread;
				pos[idx * 3 + 1] = (Math.random() - 0.5) * spread;
				pos[idx * 3 + 2] = 0;

				const brightness = 0.4 + Math.random() * 0.6;
				const colorShift = Math.random();
				let r = config.color.r * brightness;
				const g = config.color.g * brightness;
				let b = config.color.b * brightness;
				if (colorShift < 0.15) {
					r *= 1.15;
					b *= 0.85;
				} else if (colorShift < 0.3) {
					r *= 0.85;
					b *= 1.15;
				}
				col[idx * 3] = r;
				col[idx * 3 + 1] = g;
				col[idx * 3 + 2] = b;

				pf[idx] = config.parallaxFactor;
				sz[idx] = config.size;
				op[idx] = config.opacity;
			}
			offset += config.count;
		}

		return { positions: pos, colors: col, parallaxFactors: pf, sizes: sz, opacities: op };
	}, [layers, spread]);

	const material = useMemo(
		() =>
			new THREE.ShaderMaterial({
				uniforms: {
					uCameraOffset: { value: new THREE.Vector2(0, 0) },
				},
				vertexShader: VERTEX_SHADER,
				fragmentShader: FRAGMENT_SHADER,
				transparent: true,
				depthWrite: false,
			}),
		[],
	);

	useFrame(() => {
		const cam = smoothedCameraRef.current;
		const u = material.uniforms.uCameraOffset;
		if (u) (u.value as THREE.Vector2).set(cam.x, cam.y);
	});

	return (
		<group key={`parallax-stars-${viewMode}`}>
			<points renderOrder={-5} material={material}>
				<bufferGeometry>
					<bufferAttribute attach="attributes-position" args={[positions, 3]} />
					<bufferAttribute attach="attributes-color" args={[colors, 3]} />
					<bufferAttribute attach="attributes-aParallaxFactor" args={[parallaxFactors, 1]} />
					<bufferAttribute attach="attributes-aSize" args={[sizes, 1]} />
					<bufferAttribute attach="attributes-aOpacity" args={[opacities, 1]} />
				</bufferGeometry>
			</points>
		</group>
	);
}
